import React, { useState, useEffect } from 'react';
import TestimonialForm from './TestimonialForm';

const API_URL = process.env.REACT_APP_API_URL || '';

const Testimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const fetchTestimonials = async () => {
    try {
      const response = await fetch(`${API_URL}/api/testimonials`);
      if (response.ok) {
        const data = await response.json();
        console.log('Testimonials loaded:', data.length);
        setTestimonials(data);
      } else {
        console.error('Failed to fetch testimonials, status:', response.status);
        setTestimonials([]);
      }
    } catch (error) {
      console.error('Error fetching testimonials:', error);
      setTestimonials([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const handleFormSuccess = () => {
    setShowForm(false);
    fetchTestimonials();
  };

  useEffect(() => {
    if (testimonials.length > 0) {
      const interval = setInterval(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % testimonials.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [testimonials.length]);

  const nextTestimonial = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? testimonials.length - 1 : prevIndex - 1
    );
  };

  const goToTestimonial = (index) => {
    setCurrentIndex(index);
  };

  return (
    <section id="testimonials" className="py-20 bg-white">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Success <span className="text-cyan">Stories</span>
          </h2>
          <div className="w-24 h-1 bg-cyan mx-auto mb-6"></div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-6">
            Hear from athletes who have transformed their game at The Lab
          </p>
          <button
            onClick={() => setShowForm(!showForm)}
            className="bg-cyan hover:bg-cyan-600 text-white px-6 py-3 rounded-lg font-semibold transition-all transform hover:scale-105"
          >
            {showForm ? 'Hide Form' : 'Share Your Story'}
          </button>
        </div>

        {showForm && (
          <div className="max-w-2xl mx-auto mb-16">
            <TestimonialForm onSuccess={handleFormSuccess} />
          </div>
        )}

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-cyan"></div>
            <p className="mt-4 text-gray-600">Loading testimonials...</p>
          </div>
        ) : testimonials.length > 0 ? (
          <div className="relative max-w-4xl mx-auto">
            <div className="overflow-hidden">
              <div 
                className="flex transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(-${currentIndex * 100}%)` }}
              >
                {testimonials.map((testimonial, index) => (
                  <div 
                    key={index} 
                    className="w-full flex-shrink-0 px-4"
                  >
                    <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl p-8 md:p-12 shadow-lg mx-auto max-w-3xl">
                      <div className="flex justify-center mb-6">
                        <div className="w-24 h-24 bg-gradient-to-br from-cyan to-cyan-600 rounded-full flex items-center justify-center">
                          <span className="text-3xl font-bold text-white">{testimonial.initials}</span>
                        </div>
                      </div>
                      <div className="mb-6">
                        <div className="flex justify-center mb-4">
                          {[...Array(5)].map((_, i) => (
                            <svg key={i} className="w-6 h-6 text-cyan" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                          ))}
                        </div>
                        <p className="text-gray-700 italic leading-relaxed text-lg text-center mb-6">"{testimonial.text}"</p>
                      </div>
                      <div className="text-center border-t pt-6">
                        <p className="font-bold text-gray-900 text-2xl mb-1">{testimonial.name}</p>
                        <p className="text-cyan font-semibold text-lg">{testimonial.position}</p>
                        <p className="text-gray-600 text-lg">{testimonial.school}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <button
              onClick={prevTestimonial}
              className="absolute left-0 top-1/2 -translate-y-1/2 bg-white hover:bg-cyan text-gray-800 hover:text-white p-3 rounded-full shadow-lg transition-all transform hover:scale-110"
              aria-label="Previous testimonial"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <button
              onClick={nextTestimonial}
              className="absolute right-0 top-1/2 -translate-y-1/2 bg-white hover:bg-cyan text-gray-800 hover:text-white p-3 rounded-full shadow-lg transition-all transform hover:scale-110"
              aria-label="Next testimonial"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
            </button>

            <div className="flex justify-center items-center mt-8 gap-4">
              {testimonials.length <= 5 ? (
                <div className="flex gap-2">
                  {testimonials.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => goToTestimonial(index)}
                      className={`w-3 h-3 rounded-full transition-all ${
                        index === currentIndex 
                          ? 'bg-cyan w-8' 
                          : 'bg-gray-300 hover:bg-gray-400'
                      }`}
                      aria-label={`Go to testimonial ${index + 1}`}
                    />
                  ))}
                </div>
              ) : (
                <div className="flex items-center gap-4">
                  <div className="flex gap-2">
                    {[...Array(Math.min(5, testimonials.length))].map((_, i) => {
                      const isActive = i === Math.min(currentIndex, 4);
                      return (
                        <div
                          key={i}
                          className={`w-2 h-2 rounded-full transition-all ${
                            isActive ? 'bg-cyan w-6' : 'bg-gray-300'
                          }`}
                        />
                      );
                    })}
                  </div>
                  <div className="text-sm font-semibold text-gray-600">
                    {currentIndex + 1} / {testimonials.length}
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-600">No testimonials yet. Be the first to share your story!</p>
          </div>
        )}
      </div>
    </section>
  );
};

export default Testimonials;
